// ── 흐름 대시보드 — 수입/지출 항목별 내역 표 ──────────────────────────────────
// 색상은 전역 CSS 토큰 사용 — 다크모드 자동 적용.
// breakdown 필드가 있는 항목은 클릭 시 하위 분해(마케터별 등) 노출.

import React, { useState } from 'react';
import { COLORS, fmtAmount } from './constants';
import ManualEntryPanel from './ManualEntryPanel';

export default function ItemBreakdown({ summary, inflowEntries = [], outflowEntries = [], onChanged, showToast }) {
  if (!summary?.items) return null;
  const inflows = summary.items.inflows || [];
  const outflows = summary.items.outflows || [];
  const inflowTotal = summary.totals?.inflow || 0;
  const outflowTotal = summary.totals?.outflow || 0;
  const net = summary.totals?.net || 0;

  return (
    <div style={wrap}>
      <Card title="들어오는 돈" total={inflowTotal} totalColor={COLORS.inflow} items={inflows}
        footer={<ManualEntryPanel direction="inflow" entries={inflowEntries} onChanged={onChanged} showToast={showToast} />} />
      <Card title="나가는 돈" total={outflowTotal} totalColor={COLORS.outflow} items={outflows}
        footer={<ManualEntryPanel direction="outflow" entries={outflowEntries} onChanged={onChanged} showToast={showToast} />} />
      <NetCard net={net} />
    </div>
  );
}

function Card({ title, total, totalColor, items, footer }) {
  return (
    <section style={cardStyle}>
      <header style={headerStyle}>
        <h3 style={titleStyle}>{title}</h3>
        <span style={{ ...totalStyle, color: totalColor }}>{fmtAmount(total)}원</span>
      </header>
      <ul style={listStyle}>
        {items.map((it) => <Row key={it.key} item={it} />)}
      </ul>
      {footer}
    </section>
  );
}

function Row({ item }) {
  const [open, setOpen] = useState(false);
  const hasBreakdown = Array.isArray(item.breakdown) && item.breakdown.length > 0;
  const onClick = hasBreakdown ? () => setOpen((v) => !v) : undefined;

  return (
    <li>
      <div style={{ ...rowStyle, cursor: hasBreakdown ? 'pointer' : 'default' }} onClick={onClick}>
        <span style={labelStyle}>
          {hasBreakdown && <span style={chevronStyle}>{open ? '▼' : '▶'}</span>}
          {item.label}
          {item.granularity === 'monthly' && <span style={badgeStyle}>월</span>}
          {item.floorApplied && (
            <span
              style={floorBadgeStyle}
              title={`실제 ${fmtAmount(item.actualTotal || 0)}원 — 최소 표시 금액 ${fmtAmount(item.floor || 0)}원으로 보입니다.`}
            >
              최소
            </span>
          )}
        </span>
        <span style={amountStyle}>{fmtAmount(item.total || 0)}</span>
      </div>
      {hasBreakdown && open && (
        <ul style={breakdownListStyle}>
          {item.breakdown.map((b, i) => (
            <li key={b.userId ?? b.key ?? i} style={i === 0 ? breakdownRowStyle : { ...breakdownRowStyle, borderTop: '1px solid var(--border)' }}>
              {b.date ? <span style={breakdownDateStyle}>{b.date.slice(5).replace('-', '/')}</span> : null}
              <span style={breakdownLabelStyle}>{b.userName || b.label || `#${i + 1}`}</span>
              {b.sub ? <span style={breakdownSubStyle}>{b.sub}</span> : null}
              <span style={breakdownAmountStyle}>{fmtAmount(b.total || 0)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function NetCard({ net }) {
  const color = net >= 0 ? COLORS.positive : COLORS.negative;
  const sign = net >= 0 ? '+' : '';
  return (
    <section style={{ ...cardStyle, alignSelf: 'flex-start' }}>
      <header style={headerStyle}>
        <h3 style={titleStyle}>순흐름</h3>
        <span style={{ ...totalStyle, color }}>{sign}{fmtAmount(net)}원</span>
      </header>
      <div style={{ fontSize: 13, color: 'var(--text-sub)', padding: '0 4px' }}>
        수입 누적 - 지출 누적 = 그 달 말까지의 순흐름
      </div>
    </section>
  );
}

const wrap = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 };
const cardStyle = { border: '1px solid var(--border)', borderRadius: 8, padding: 16, background: 'var(--bg-card)' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 };
const titleStyle = { margin: 0, fontSize: 15, color: 'var(--text)' };
const totalStyle = { fontWeight: 700, fontSize: 18 };
const listStyle = { listStyle: 'none', padding: 0, margin: 0 };
const rowStyle = { display: 'flex', justifyContent: 'space-between', padding: '6px 4px', borderTop: '1px solid var(--border)', fontSize: 14 };
const labelStyle = { color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 };
const amountStyle = { color: 'var(--text)', fontVariantNumeric: 'tabular-nums' };
const badgeStyle = { fontSize: 11, padding: '1px 6px', borderRadius: 10, background: 'var(--bg-subtle)', color: 'var(--text-sub)' };
// 실제 금액이 아니라 최소 표시 금액으로 보이는 중임을 알리는 표식 — 실적으로 오해하지 않게.
const floorBadgeStyle = { fontSize: 11, padding: '1px 6px', borderRadius: 10, background: 'var(--mm-tone-amber-bg)', color: 'var(--mm-tone-amber-text)', border: '1px solid var(--mm-tone-amber-border)', cursor: 'help' };
const chevronStyle = { fontSize: 10, color: 'var(--text-sub)', width: 12, display: 'inline-block' };
const breakdownListStyle = { listStyle: 'none', padding: '4px 4px 6px 24px', margin: 0, background: 'var(--bg-subtle)' };
const breakdownRowStyle = { display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 4px', fontSize: 13 };
// label 이 남는 폭을 차지하고 금액은 우측 끝 — date/sub 없는 항목도 동일하게 좌/우 정렬됨
const breakdownLabelStyle = { color: 'var(--text-sub)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const breakdownDateStyle = { color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', flexShrink: 0, width: 38 };
const breakdownSubStyle = { color: 'var(--text-muted)', fontSize: 12, flexShrink: 0 };
const breakdownAmountStyle = { color: 'var(--text)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 };
